import { zodResolver } from "@hookform/resolvers/zod";
import { SubmitHandler, useForm } from "react-hook-form";
import StyledSpinner from "@/components/styled/StyledSpinner";
import { useUpdateCompanyParametersBO } from "@/api/company.api";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import {
  ParametersTarifFormData,
  ParametersTarifSchema,
} from "@/types/forms/bo/clientsParameters.form";
import { useGetTarifs } from "@/api/tarif.api";
import { ControlledSelectV2 } from "@/components/ui/customs/controlled-select-v2";
import { Modal } from "@/components/ui/customs/modal";
import { Button } from "@/components/ui/button";

type UpdateTarifModalProps = {
  closeModal: () => void;
  tarifId: string;
  title: string;
};

export const UpdateTarifModal = ({ closeModal, tarifId, title }: UpdateTarifModalProps) => {
  const { t } = useTranslation("boClients");
  const [companyId] = useLocation().pathname.split("/").slice(-1);
  const { data: tarifs } = useGetTarifs();

  const { mutateAsync: updateCompanyParametersBO, isPending } = useUpdateCompanyParametersBO();

  const tarifForm = useForm<ParametersTarifFormData>({
    resolver: zodResolver(ParametersTarifSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: {
      tarifId: tarifId,
    },
  });

  const onSubmit: SubmitHandler<ParametersTarifFormData> = async (data) => {
    const ret = await updateCompanyParametersBO({
      id: companyId,
      payload: data,
    });
    if (ret?.status === 200) {
      closeModal();
    }
  };

  const tarifOptions =
    tarifs?.data
      ?.filter((tarif) => !tarif.isExpress)
      .map((tarif) => ({
        label: tarif.label,
        value: tarif.id,
      })) || [];

  return (
    <Modal
      open
      onClose={closeModal}
      title={title}
      size="sm"
      contentClassName="max-w-[480px]"
      footer={
        <Button type="submit" form="tarifForm" disabled={isPending} className="w-full">
          {isPending ? <StyledSpinner /> : t("tarifModal.submit")}
        </Button>
      }
    >
      <form
        id="tarifForm"
        onSubmit={tarifForm.handleSubmit(onSubmit)}
        className="flex flex-col gap-4 pt-2"
      >
        <ControlledSelectV2
          options={tarifOptions}
          control={tarifForm.control}
          name="tarifId"
          contentClassName="w-full"
        />
      </form>
    </Modal>
  );
};
